import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BASEURL_CLIENT_BACKEND || 'http://localhost:4000';

class StockService {
    constructor() {
        this.baseURL = `${API_BASE_URL}/Stock`;
        this.mvpBaseURL = `${API_BASE_URL}/api/StockMvp`;

        // Vérification de la configuration en mode développement uniquement
        if (process.env.NODE_ENV === 'development') {
            console.info('StockService configuré:', {
                API_BASE_URL,
                baseURL: this.baseURL,
                mvpBaseURL: this.mvpBaseURL,
                env_variable: process.env.REACT_APP_BASEURL_CLIENT_BACKEND
            });
        }

        // Configuration par défaut d'axios
        axios.defaults.headers.common['Content-Type'] = 'application/json';

        // Intercepteur pour ajouter le token d'authentification
        axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Intercepteur pour gérer les erreurs de réponse
        axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    // Token expiré ou invalide
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    // ==================== STOCK REPORTS OPERATIONS ====================

    /**
     * Récupère les rapports de mouvement de stock
     */
    async getStockMovementReport(filters = {}) {
        try {
            // Use movement history endpoint (UC-015)
            const query = {
                stockId: filters.stockId || null,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                stockType: filters.stockType,
                movementType: filters.category
            };
            const response = await axios.post(`${this.mvpBaseURL}/movement/history`, query);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération du rapport de mouvement:', error);
            // Return empty array as fallback
            if (error.response?.status === 500 || error.response?.status === 404) {
                return { movements: [], totalCount: 0 };
            }
            throw error;
        }
    }

    /**
     * Récupère les rapports de valeur de stock
     */
    async getStockValueReport(filters = {}) {
        try {
            // Use main controller value-by-type endpoint
            const response = await axios.get(`${this.baseURL}/value-by-type`);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération du rapport de valeur:', error);
            if (error.response?.status === 500 || error.response?.status === 404) {
                return {};
            }
            throw error;
        }
    }

    /**
     * Récupère le rapport des stocks faibles
     */
    async getLowStockReport() {
        try {
            // Use main controller low-stock endpoint
            const response = await axios.get(`${this.baseURL}/low-stock`);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération du rapport stock faible:', error);
            if (error.response?.status === 500 || error.response?.status === 404) {
                return [];
            }
            throw error;
        }
    }

    /**
     * Récupère le rapport des stocks expirés
     */
    async getExpiredStockReport() {
        try {
            // Use main controller expired endpoint
            const response = await axios.get(`${this.baseURL}/expired`);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération du rapport stock expiré:', error);
            if (error.response?.status === 500 || error.response?.status === 404) {
                return [];
            }
            throw error;
        }
    }

    /**
     * Récupère le rapport des produits les plus utilisés
     */
    async getTopProductsReport(limit = 10) {
        try {
            // This endpoint doesn't exist yet, use summary with client-side sorting
            const summary = await this.getStockSummary();
            // Sort by total value and take top N
            return summary
                .sort((a, b) => (b.totalValue || 0) - (a.totalValue || 0))
                .slice(0, limit);
        } catch (error) {
            console.error('Erreur lors de la récupération du rapport top produits:', error);
            return [];
        }
    }

    /**
     * Récupère l'analyse des coûts
     */
    async getCostAnalysisReport(filters = {}) {
        try {
            // Use stock report endpoint for cost analysis
            const fromDate = filters.fromDate || new Date(new Date().setMonth(new Date().getMonth() - 1));
            const toDate = filters.toDate || new Date();
            const response = await axios.get(`${this.baseURL}/report`, {
                params: { fromDate, toDate }
            });
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération de l\'analyse des coûts:', error);
            if (error.response?.status === 500 || error.response?.status === 404) {
                return [];
            }
            throw error;
        }
    }

    /**
     * Génère et télécharge un rapport Excel
     */
    async downloadReportExcel(reportType, filters = {}) {
        try {
            // This endpoint doesn't exist - just log a warning for now
            console.warn('Excel export not yet implemented for:', reportType);
            return false;
        } catch (error) {
            console.error('Erreur lors du téléchargement du rapport:', error);
            throw error;
        }
    }

    /**
     * Récupère les statistiques du tableau de bord
     */
    async getDashboardStats() {
        try {
            // Use getDashboardData which has fallback logic
            return await this.getDashboardData();
        } catch (error) {
            console.error('Erreur lors de la récupération des statistiques dashboard:', error);
            throw error;
        }
    }

    /**
     * Récupère les tendances de consommation
     */
    async getConsumptionTrends(period = '30days') {
        try {
            // Use consumption report endpoint
            const fromDate = new Date();
            fromDate.setDate(fromDate.getDate() - (period === '30days' ? 30 : period === '7days' ? 7 : 90));
            const toDate = new Date();
            const response = await axios.get(`${this.baseURL}/consumption-report`, {
                params: { fromDate, toDate }
            });
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération des tendances:', error);
            if (error.response?.status === 500 || error.response?.status === 404) {
                return [];
            }
            throw error;
        }
    }

    /**
     * Récupère les alertes de stock
     */
    async getStockAlerts() {
        try {
            // Use main controller alerts endpoint
            return await this.getActiveAlerts();
        } catch (error) {
            console.error('Erreur lors de la récupération des alertes:', error);
            return [];
        }
    }

    // ==================== STOCK PAYMENT OPERATIONS ====================

    async getStockPaymentInfo(stockId) {
        try {
            const response = await axios.get(`${this.mvpBaseURL}/${stockId}/payment-info`);
            return response.data;
        } catch (error) {
            console.error('Error getting stock payment info:', error);
            throw this.handleError(error);
        }
    }

    async checkStockPaymentEligibility(stockId) {
        try {
            const response = await axios.get(`${this.mvpBaseURL}/${stockId}/payment-eligibility`);
            return response.data;
        } catch (error) {
            console.error('Error checking payment eligibility:', error);
            throw this.handleError(error);
        }
    }

    async calculateStockPaymentCost(stockId, paymentType = 'Standard') {
        try {
            const response = await axios.post(`${this.mvpBaseURL}/${stockId}/calculate-cost`, {
                paymentType
            });
            return response.data;
        } catch (error) {
            console.error('Error calculating payment cost:', error);
            throw this.handleError(error);
        }
    }

    async processStockPayment(paymentData) {
        try {
            const response = await axios.post(`${this.mvpBaseURL}/process-payment`, paymentData);
            return response.data;
        } catch (error) {
            console.error('Error processing stock payment:', error);
            throw this.handleError(error);
        }
    }

    async processBulkStockPayment(bulkPaymentData) {
        try {
            const response = await axios.post(`${this.mvpBaseURL}/bulk-payment`, bulkPaymentData);
            return response.data;
        } catch (error) {
            console.error('Error processing bulk stock payment:', error);
            throw this.handleError(error);
        }
    }

    // ==================== STOCK MANAGEMENT ====================

    async getStockSummary() {
        try {
            // Use main controller for detailed stock items (not MVP summary)
            const response = await axios.get(`${this.baseURL}/summary`);
            if (process.env.NODE_ENV === 'development') {
                console.log('Stock Summary API success - Items count:', response.data.length);
            }
            return response.data;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Error getting stock summary:', error);
            }

            // Return empty array as fallback to prevent UI breaking
            if (error.response?.status === 500 || error.response?.status === 404) {
                console.warn('Stock summary API failed, returning empty array');
                return [];
            }

            throw this.handleError(error);
        }
    }

    async getStockById(stockId) {
        try {
            const response = await axios.get(`${this.baseURL}/${stockId}`);
            return response.data;
        } catch (error) {
            // Return null if stock not found instead of throwing
            if (error.response?.status === 404 || error.response?.status === 500) {
                return null;
            }
            console.error('Error getting stock by ID:', error);
            throw this.handleError(error);
        }
    }

    async getStockByProductCode(productCode) {
        try {
            const response = await axios.get(`${this.baseURL}/product/${productCode}`);
            return response.data;
        } catch (error) {
            console.error('Error getting stock by product code:', error);
            throw this.handleError(error);
        }
    }

    async getStocksByType(stockType) {
        try {
            const response = await axios.get(`${this.baseURL}/type/${stockType}`);
            return response.data;
        } catch (error) {
            console.error('Error getting stocks by type:', error);
            throw this.handleError(error);
        }
    }

    async getLowStockItems() {
        try {
            const response = await axios.get(`${this.baseURL}/low-stock`);
            return response.data;
        } catch (error) {
            console.error('Error getting low stock items:', error);
            throw this.handleError(error);
        }
    }

    async getCriticalStockItems() {
        try {
            const response = await axios.get(`${this.baseURL}/critical-stock`);
            return response.data;
        } catch (error) {
            console.error('Error getting critical stock items:', error);
            throw this.handleError(error);
        }
    }

    async getExpiringSoon(daysBefore = 30) {
        try {
            const response = await axios.get(`${this.baseURL}/expiring-soon?daysBefore=${daysBefore}`);
            return response.data;
        } catch (error) {
            console.error('Error getting expiring stock items:', error);
            throw this.handleError(error);
        }
    }

    async getExpiredStock() {
        try {
            const response = await axios.get(`${this.baseURL}/expired`);
            return response.data;
        } catch (error) {
            console.error('Error getting expired stock items:', error);
            throw this.handleError(error);
        }
    }

    // ==================== STOCK OPERATIONS ====================

    async createStock(stockData) {
        try {
            const response = await axios.post(`${this.baseURL}`, stockData);
            return response.data;
        } catch (error) {
            console.error('Error creating stock:', error);
            throw this.handleError(error);
        }
    }

    async updateStock(stockId, updateData) {
        try {
            const response = await axios.put(`${this.baseURL}/${stockId}`, updateData);
            return response.data;
        } catch (error) {
            // Return false if update fails
            if (error.response?.status === 500 || error.response?.status === 404) {
                return false;
            }
            console.error('Error updating stock:', error);
            throw this.handleError(error);
        }
    }

    async deactivateStock(stockId, reason, userId = 'CURRENT_USER') {
        try {
            const response = await axios.delete(`${this.baseURL}/${stockId}`, {
                data: { reason, userId }
            });
            return response.data;
        } catch (error) {
            console.error('Error deactivating stock:', error);
            throw this.handleError(error);
        }
    }

    async restock(stockId, restockData) {
        try {
            const data = {
                stockId: stockId,
                ...restockData,
                restockedBy: 'CURRENT_USER' // TODO: Get actual user ID
            };
            // Use MVP endpoint for restock operations
            const response = await axios.post(`${this.mvpBaseURL}/restock`, data);
            return response.data;
        } catch (error) {
            console.error('Error restocking:', error);
            throw this.handleError(error);
        }
    }

    async restockStandard(stockId, restockData) {
        try {
            // Use standard Stock controller endpoint for restock
            const response = await axios.post(`${this.baseURL}/${stockId}/restock`, restockData);
            return response.data;
        } catch (error) {
            console.error('Error restocking (standard):', error);
            throw this.handleError(error);
        }
    }

    async adjustStock(stockId, newQuantity, reason, userId = 'CURRENT_USER') {
        try {
            const response = await axios.post(`${this.baseURL}/${stockId}/adjust`, {
                newQuantity,
                reason,
                userId
            });
            return response.data;
        } catch (error) {
            console.error('Error adjusting stock:', error);
            throw this.handleError(error);
        }
    }

    async markDefective(stockId, quantity, reason, userId = 'CURRENT_USER') {
        try {
            const response = await axios.post(`${this.baseURL}/${stockId}/mark-defective`, {
                quantity,
                reason,
                userId
            });
            return response.data;
        } catch (error) {
            console.error('Error marking defective stock:', error);
            throw this.handleError(error);
        }
    }

    // ==================== STOCK MOVEMENTS ====================

    async getStockMovements(stockId, fromDate = null, toDate = null) {
        try {
            let url = `${this.baseURL}/${stockId}/movements`;
            const params = new URLSearchParams();

            if (fromDate) params.append('fromDate', fromDate);
            if (toDate) params.append('toDate', toDate);

            if (params.toString()) url += `?${params.toString()}`;

            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error('Error getting stock movements:', error);
            throw this.handleError(error);
        }
    }

    async getMovementSummary(stockId, fromDate, toDate) {
        try {
            const response = await axios.get(`${this.baseURL}/${stockId}/movement-summary`, {
                params: { fromDate, toDate }
            });
            return response.data;
        } catch (error) {
            console.error('Error getting movement summary:', error);
            throw this.handleError(error);
        }
    }

    // ==================== STOCK AVAILABILITY ====================

    async checkStockAvailability(batchSize, templateId = null) {
        try {
            const response = await axios.post(`${this.baseURL}/check-availability`, {
                batchSize,
                templateId
            });
            return response.data;
        } catch (error) {
            console.error('Error checking stock availability:', error);
            throw this.handleError(error);
        }
    }

    async reserveStock(batchId, requirements, userId = 'CURRENT_USER') {
        try {
            const response = await axios.post(`${this.baseURL}/reserve`, {
                batchId,
                requirements,
                userId
            });
            return response.data;
        } catch (error) {
            console.error('Error reserving stock:', error);
            throw this.handleError(error);
        }
    }

    async releaseReservedStock(batchId, userId = 'CURRENT_USER') {
        try {
            const response = await axios.post(`${this.baseURL}/release-reservation`, {
                batchId,
                userId
            });
            return response.data;
        } catch (error) {
            console.error('Error releasing reserved stock:', error);
            throw this.handleError(error);
        }
    }

    // ==================== ALERTS ====================

    async getActiveAlerts() {
        try {
            // Use main Stock controller for alerts (not MVP)
            const response = await axios.get(`${this.baseURL}/alerts`);
            return response.data;
        } catch (error) {
            // Return empty array as fallback for alerts
            if (error.response?.status === 500 || error.response?.status === 404) {
                return [];
            }
            console.error('Error getting active alerts:', error);
            throw this.handleError(error);
        }
    }

    async getAlertsForStock(stockId) {
        try {
            const response = await axios.get(`${this.baseURL}/${stockId}/alerts`);
            return response.data;
        } catch (error) {
            console.error('Error getting alerts for stock:', error);
            throw this.handleError(error);
        }
    }

    async acknowledgeAlert(alertId, userId = 'CURRENT_USER') {
        try {
            const response = await axios.post(`${this.baseURL}/alerts/${alertId}/acknowledge`, {
                userId
            });
            return response.data;
        } catch (error) {
            // Return success false if acknowledgment fails
            if (error.response?.status === 500 || error.response?.status === 404) {
                return { success: false, message: 'Unable to acknowledge alert' };
            }
            console.error('Error acknowledging alert:', error);
            throw this.handleError(error);
        }
    }

    async generateAlerts() {
        try {
            const response = await axios.post(`${this.baseURL}/generate-alerts`);
            return response.data;
        } catch (error) {
            console.error('Error generating alerts:', error);
            throw this.handleError(error);
        }
    }

    // ==================== DASHBOARD & REPORTING ====================

    async getDashboardData() {
        try {
            if (process.env.NODE_ENV === 'development') {
                console.log('Calling dashboard API:', `${this.mvpBaseURL}/dashboard`);
            }
            // Use MVP endpoint for dashboard data
            const response = await axios.get(`${this.mvpBaseURL}/dashboard`);
            if (process.env.NODE_ENV === 'development') {
                console.log('Dashboard API success:', response.data);
            }
            return response.data;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.log('MVP dashboard failed, trying fallback:', error.response?.status, error.message);
            }
            // Fallback to main controller dashboard
            try {
                const fallbackResponse = await axios.get(`${this.baseURL}/dashboard`);
                if (process.env.NODE_ENV === 'development') {
                    console.log('Fallback dashboard API success:', fallbackResponse.data);
                }
                return fallbackResponse.data;
            } catch (fallbackError) {
                if (process.env.NODE_ENV === 'development') {
                    console.log('Both dashboard APIs failed:', fallbackError.response?.status, fallbackError.message);
                }
                // Return mock dashboard data as ultimate fallback
                if (fallbackError.response?.status === 500 || fallbackError.response?.status === 404) {
                    const mockData = {
                        totalStockItems: 0,
                        totalStockValue: 0,
                        lowStockItemsCount: 0,
                        criticalStockItemsCount: 0,
                        expiringSoonCount: 0,
                        expiredItemsCount: 0,
                        activeAlertsCount: 0,
                        recentAlerts: [],
                        topLowStockItems: [],
                        stockByType: {},
                        valueByType: {},
                        consumptionTrends: []
                    };
                    if (process.env.NODE_ENV === 'development') {
                        console.log('Returning mock dashboard data:', mockData);
                    }
                    return mockData;
                }
                console.error('Error getting dashboard data:', error);
                throw this.handleError(error);
            }
        }
    }

    async getStockReport(fromDate, toDate) {
        try {
            const response = await axios.get(`${this.baseURL}/report`, {
                params: { fromDate, toDate }
            });
            return response.data;
        } catch (error) {
            // Return empty report as fallback
            if (error.response?.status === 500 || error.response?.status === 404) {
                return [];
            }
            console.error('Error getting stock report:', error);
            throw this.handleError(error);
        }
    }

    async getConsumptionReport(fromDate, toDate) {
        try {
            const response = await axios.get(`${this.baseURL}/consumption-report`, {
                params: { fromDate, toDate }
            });
            return response.data;
        } catch (error) {
            // Return empty report as fallback
            if (error.response?.status === 500 || error.response?.status === 404) {
                return [];
            }
            console.error('Error getting consumption report:', error);
            throw this.handleError(error);
        }
    }

    async getStockValueByType() {
        try {
            const response = await axios.get(`${this.baseURL}/value-by-type`);
            return response.data;
        } catch (error) {
            console.error('Error getting stock value by type:', error);
            throw this.handleError(error);
        }
    }

    // ==================== UTILITY METHODS ====================

    /**
     * Gère les erreurs de l'API et retourne un message d'erreur approprié
     */
    handleError(error) {
        if (error.response) {
            // Le serveur a répondu avec un code d'erreur
            const { status, data } = error.response;

            switch (status) {
                case 400:
                    return new Error(data.message || 'Données invalides');
                case 401:
                    return new Error('Non autorisé - Veuillez vous connecter');
                case 403:
                    return new Error('Accès interdit - Droits insuffisants');
                case 404:
                    return new Error(data.message || 'Ressource non trouvée');
                case 409:
                    return new Error(data.message || 'Conflit de données');
                case 500:
                    return new Error(data.message || 'Erreur interne du serveur');
                default:
                    return new Error(data.message || `Erreur HTTP ${status}`);
            }
        } else if (error.request) {
            // La requête a été faite mais aucune réponse n'a été reçue
            return new Error('Pas de réponse du serveur - Vérifiez votre connexion');
        } else {
            // Erreur lors de la configuration de la requête
            return new Error(error.message || 'Erreur de configuration de la requête');
        }
    }

    /**
     * Formate les données pour l'affichage
     */
    formatCurrency(amount) {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'XOF', // Franc CFA
            minimumFractionDigits: 0
        }).format(amount);
    }

    formatDate(dateString) {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    formatStockStatus(stock) {
        if (!stock.isActive) return { text: 'Inactif', variant: 'secondary' };
        if (stock.currentQuantity === 0) return { text: 'Rupture', variant: 'danger' };
        if (stock.isLowStock) return { text: 'Stock faible', variant: 'warning' };
        if (stock.isExpiringSoon) return { text: 'Expire bientôt', variant: 'info' };
        return { text: 'Normal', variant: 'success' };
    }

    /**
     * Calcule les statistiques rapides d'un stock
     */
    calculateStockStats(stockItems) {
        return {
            total: stockItems.length,
            active: stockItems.filter(item => item.isActive).length,
            lowStock: stockItems.filter(item => item.isLowStock).length,
            outOfStock: stockItems.filter(item => item.currentQuantity === 0).length,
            expiringSoon: stockItems.filter(item => item.isExpiringSoon).length,
            totalValue: stockItems.reduce((sum, item) => sum + item.totalValue, 0)
        };
    }
}

// Instance unique du service
export const stockService = new StockService();
export default StockService;